/**
 * useDocumentation Hook
 *
 * Provides context-aware documentation retrieval for EnergyPlus objects and fields.
 * Integrates IDD schema documentation with engineering reference content.
 */

import { useMemo, useCallback } from 'react';
import { useIDDStore } from '@/stores/iddStore';
import { useDocsStore } from '../store/docsStore';
import type { FieldDocumentation, ObjectDocumentation, EngineeringDocument } from '../types';

export interface EnhancedFieldDocumentation extends FieldDocumentation {
  /** Full engineering reference document (not just the string reference) */
  engineeringDocument?: EngineeringDocument | undefined;
  /** Related objects that use this field type */
  relatedObjectsList?: string[] | undefined;
}

export interface EnhancedObjectDocumentation extends ObjectDocumentation {
  /** Engineering documents related to this object */
  engineeringDocs: EngineeringDocument[];
}

export interface UseDocumentationReturn {
  /** Whether the documentation system is ready */
  isReady: boolean;

  /** Whether documentation is currently loading */
  isLoading: boolean;

  /** Get enhanced documentation for a field */
  getFieldDocumentation: (
    objectType: string | undefined,
    fieldName: string
  ) => EnhancedFieldDocumentation | null;

  /** Get enhanced documentation for an object */
  getObjectDocumentation: (objectType: string) => EnhancedObjectDocumentation | null;

  /** Get engineering reference documents for an object */
  getEngineeringDocs: (objectType: string) => EngineeringDocument[];

  /** Open the documentation panel for an object/field */
  openDocsPanel: (objectType?: string, fieldName?: string) => void;

  /** Close the documentation panel */
  closeDocsPanel: () => void;

  /** Check if an object has engineering reference content */
  hasEngineeringRef: (objectType: string) => boolean;
}

/**
 * Hook for accessing context-aware documentation
 */
export function useDocumentation(): UseDocumentationReturn {
  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);

  const {
    isSearchReady,
    isLoading,
    getObjectDocumentation: getBasicObjectDocs,
    getFieldDocumentation: getBasicFieldDocs,
    getEngineeringDocsForObject,
    getEngineeringRef,
    openPanel,
    closePanel,
  } = useDocsStore();

  const isReady = isSchemaLoaded && isSearchReady;

  const getFieldDocumentation = useCallback(
    (objectType: string | undefined, fieldName: string): EnhancedFieldDocumentation | null => {
      if (!objectType || !schema) return null;

      const fieldDoc = getBasicFieldDocs(objectType, fieldName, schema);
      if (!fieldDoc) return null;

      // Find related engineering reference
      const engineeringDocument = getEngineeringRef(objectType);

      // Find related objects that reference similar field types
      const relatedObjectsList: string[] = [];
      if (fieldDoc.objectList) {
        const refs = schema.referenceLists.get(fieldDoc.objectList);
        if (refs) {
          relatedObjectsList.push(...Array.from(refs).slice(0, 5));
        }
      }

      return {
        ...fieldDoc,
        engineeringDocument: engineeringDocument ?? undefined,
        relatedObjectsList: relatedObjectsList.length > 0 ? relatedObjectsList : undefined,
      };
    },
    [schema, getBasicFieldDocs, getEngineeringRef]
  );

  const getObjectDocumentation = useCallback(
    (objectType: string): EnhancedObjectDocumentation | null => {
      if (!schema) return null;

      const basicDocs = getBasicObjectDocs(objectType, schema);
      if (!basicDocs) return null;

      const engineeringDocs = getEngineeringDocsForObject(objectType);

      return {
        ...basicDocs,
        engineeringDocs,
      };
    },
    [schema, getBasicObjectDocs, getEngineeringDocsForObject]
  );

  const getEngineeringDocs = useCallback(
    (objectType: string): EngineeringDocument[] => {
      return getEngineeringDocsForObject(objectType);
    },
    [getEngineeringDocsForObject]
  );

  const hasEngineeringRef = useCallback(
    (objectType: string): boolean => {
      return getEngineeringRef(objectType) !== null;
    },
    [getEngineeringRef]
  );

  const openDocsPanel = useCallback(
    (objectType?: string, fieldName?: string) => {
      openPanel(objectType, fieldName);
    },
    [openPanel]
  );

  const closeDocsPanel = useCallback(() => {
    closePanel();
  }, [closePanel]);

  return useMemo(
    () => ({
      isReady,
      isLoading,
      getFieldDocumentation,
      getObjectDocumentation,
      getEngineeringDocs,
      openDocsPanel,
      closeDocsPanel,
      hasEngineeringRef,
    }),
    [
      isReady,
      isLoading,
      getFieldDocumentation,
      getObjectDocumentation,
      getEngineeringDocs,
      openDocsPanel,
      closeDocsPanel,
      hasEngineeringRef,
    ]
  );
}

/**
 * Hook for documentation search functionality
 */
export function useDocsSearch() {
  const { searchDocs, searchDocsFiltered, isSearchReady } = useDocsStore();

  const search = useCallback(
    (query: string, limit = 20) => {
      if (!isSearchReady || !query.trim()) return [];
      return searchDocs(query, limit);
    },
    [isSearchReady, searchDocs]
  );

  const searchFiltered = useCallback(
    (
      query: string,
      options?: { type?: 'object' | 'field' | 'engineering'; group?: string; limit?: number }
    ) => {
      if (!isSearchReady || !query.trim()) return [];
      return searchDocsFiltered(query, options);
    },
    [isSearchReady, searchDocsFiltered]
  );

  return useMemo(
    () => ({
      search,
      searchFiltered,
      isReady: isSearchReady,
    }),
    [search, searchFiltered, isSearchReady]
  );
}

/**
 * Hook for related documentation content
 */
export function useRelatedDocs(objectType: string | undefined) {
  const schema = useIDDStore((state) => state.schema);
  const isSchemaLoaded = useIDDStore((state) => state.isLoaded);
  const getRelatedObjects = useDocsStore((state) => state.getRelatedObjects);
  const getEngineeringDocsForObject = useDocsStore((state) => state.getEngineeringDocsForObject);

  const relatedObjects = useMemo(() => {
    if (!objectType || !schema || !isSchemaLoaded) return [];
    return getRelatedObjects(objectType, schema);
  }, [objectType, schema, isSchemaLoaded, getRelatedObjects]);

  const engineeringDocs = useMemo(() => {
    if (!objectType) return [];
    return getEngineeringDocsForObject(objectType);
  }, [objectType, getEngineeringDocsForObject]);

  return useMemo(
    () => ({
      relatedObjects,
      engineeringDocs,
      hasRelated: relatedObjects.length > 0,
      hasEngineeringDocs: engineeringDocs.length > 0,
    }),
    [relatedObjects, engineeringDocs]
  );
}
